import { Copy, Pencil, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { LocalTime } from '~/components/common/LocalTime';
import { ListToolbar, PageHeader, PageShell } from '~/components/layout';
import { aiStatus } from '~/components/pages/ai/common/statusMaps';
import { PERSONA_STATUS_OPTIONS } from '~/lib/ai/personaForm';
import {
  Alert,
  Card,
  CardBody,
  ConfirmModal,
  DataTable,
  Pagination,
  RowActionsMenu,
  type DataTableColumn,
  CopyButton,
  StatusSelect,
} from '~/components/ui';
import { useListUrlState } from '~/hooks/useListUrlState';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import { matchesPersonaSearch } from '~/lib/ai/listFilters';
import { clampPage, pageCount } from '~/lib/listParams';
import {
  useDeletePersonaMutation,
  useDuplicatePersonaMutation,
  useListPersonasQuery,
  useUpdatePersonaMutation,
} from '~/modules/query/ai/personas';
import type { PersonaListItem } from '~/modules/query/ai/types';
import { routes } from '~/app/routes';

export default function PersonasPage() {
  const list = useListUrlState();
  const personas = useListPersonasQuery();
  const [duplicatePersona] = useDuplicatePersonaMutation();
  const [deletePersona] = useDeletePersonaMutation();
  const [updatePersona] = useUpdatePersonaMutation();
  const [statusSavingId, setStatusSavingId] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<PersonaListItem | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const all = useMemo(() => personas.data?.data ?? [], [personas.data]);

  const rows = useMemo(
    () => all.filter((persona) => matchesPersonaSearch(persona, list.q)),
    [all, list.q],
  );

  const pages = pageCount(rows.length, list.pageSize);
  const page = clampPage(list.page, rows.length, list.pageSize);
  const pageRows = rows.slice((page - 1) * list.pageSize, page * list.pageSize);

  const duplicate = async (persona: PersonaListItem) => {
    setActionError(null);
    setDuplicatingId(persona.id);
    try {
      await duplicatePersona(persona.id).unwrap();
      toast.success('Persona duplicated');
    } catch (error) {
      setActionError(
        apiErrorMessage(error as ApiErrorInput) ??
          'The persona could not be duplicated. Try again.',
      );
    } finally {
      setDuplicatingId(null);
    }
  };

  const changeStatus = async (persona: PersonaListItem, status: string) => {
    const next = PERSONA_STATUS_OPTIONS.find((option) => option.value === status);

    if (!next || status === persona.status) return;
    setActionError(null);
    setStatusSavingId(persona.id);
    try {
      await updatePersona({ id: persona.id, status: next.value }).unwrap();
      toast.success(`Status updated for ${persona.name}`);
    } catch (error) {
      setActionError(
        apiErrorMessage(error as ApiErrorInput) ?? 'The status could not be updated. Try again.',
      );
    } finally {
      setStatusSavingId(null);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setActionError(null);
    try {
      await deletePersona(deleting.id).unwrap();
      toast.success('Persona deleted');
      setDeleting(null);
    } catch (error) {
      setDeleting(null);
      setActionError(
        apiErrorMessage(error as ApiErrorInput) ?? 'The persona could not be deleted. Try again.',
      );
    }
  };

  const columns: DataTableColumn<PersonaListItem>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (persona) => (
        <div>
          <p className="font-medium text-gray-800 dark:text-white/90">{persona.name}</p>
          <div className="flex items-center gap-1">
            <p className="font-mono text-xs text-gray-500 dark:text-gray-400">{persona.id}</p>
            <CopyButton
              value={persona.id}
              label={`Copy persona ID ${persona.id}`}
              toastMessage="Persona ID copied"
              testId={`personas-copy-${persona.id}`}
            />
          </div>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (persona) => (
        <StatusSelect
          value={persona.status}
          options={PERSONA_STATUS_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          toneOf={(status) => aiStatus(status).tone}
          disabled={statusSavingId === persona.id}
          ariaLabel={`Status of ${persona.name}`}
          testId={`personas-status-${persona.id}`}
          onChange={(status) => void changeStatus(persona, status)}
        />
      ),
    },
    {
      id: 'description',
      header: 'Description',
      cell: (persona) =>
        persona.description ? (
          <p className="max-w-md truncate" title={persona.description}>
            {persona.description}
          </p>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">—</span>
        ),
    },
    {
      id: 'updated',
      header: 'Updated',
      cell: (persona) => (
        <div>
          <LocalTime value={persona.updatedAt} format="date" />
          {persona.updatedBy && (
            <p className="text-xs text-gray-500 dark:text-gray-400">by {persona.updatedBy}</p>
          )}
        </div>
      ),
    },
    {
      id: 'created',
      header: 'Created',
      cell: (persona) => (
        <div>
          <LocalTime value={persona.createdAt} format="date" />
          {persona.createdBy && (
            <p className="text-xs text-gray-500 dark:text-gray-400">by {persona.createdBy}</p>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (persona) => (
        <RowActionsMenu
          testId={`personas-actions-${persona.id}`}
          ariaLabel={`Open actions for ${persona.name}`}
          items={[
            {
              label: 'Edit',
              testId: 'edit',
              icon: <Pencil className="size-4" aria-hidden="true" />,
              href: routes.aiPersona(persona.id),
            },
            {
              label: 'Duplicate',
              testId: 'duplicate',
              icon: <Copy className="size-4" aria-hidden="true" />,
              disabled: duplicatingId === persona.id,
              disabledReason: 'This persona is being duplicated.',
              onClick: () => duplicate(persona),
            },
            {
              label: 'Delete',
              testId: 'delete',
              icon: <Trash2 className="size-4" aria-hidden="true" />,
              destructive: true,
              disabled: duplicatingId === persona.id,
              disabledReason: 'This persona is being duplicated.',
              onClick: () => setDeleting(persona),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <PageShell>
        <PageHeader
          title="Personas"
          description="The role, tone and guardrails the AI uses when a placement references a persona."
          breadcrumbs={[{ label: 'AI' }, { label: 'Personas' }]}
          actions={[
            {
              label: 'Create persona',
              kind: 'primary',
              href: routes.aiPersonaNew,
              testId: 'personas-new-button',
            },
          ]}
        />

        {actionError && (
          <Alert variant="error">
            <p className="text-sm text-gray-800 dark:text-white/90">{actionError}</p>
          </Alert>
        )}

        <Card>
          <ListToolbar
            title="Personas"
            search={{
              value: list.q,
              onChange: list.setQ,
              placeholder: 'Search personas…',
              debounceMs: 0,
              testId: 'personas-search',
            }}
          />
          <CardBody className="p-0">
            <DataTable<PersonaListItem>
              framed={false}
              testId="personas-table"
              loading={!personas.data}
              refreshing={personas.isFetching && !!personas.data}
              data={pageRows}
              rowKey={(persona) => persona.id}
              href={(persona) => routes.aiPersona(persona.id)}
              rowLabel={(persona) => `Open ${persona.name}`}
              columns={columns}
              error={
                personas.error && !personas.data
                  ? {
                      what: 'personas',
                      message: apiErrorMessage(personas.error as ApiErrorInput),
                      onRetry: () => personas.refetch(),
                    }
                  : undefined
              }
              emptyMessage={list.q ? 'No personas match.' : 'No personas yet.'}
              emptyDescription={
                list.q
                  ? 'Clear the search or try another name.'
                  : 'Create a persona to set the baseline voice for AI writing across the platform.'
              }
            />
          </CardBody>
          <Pagination
            pageIndex={page - 1}
            pageSize={list.pageSize}
            pageCount={pages}
            totalRows={rows.length}
            onPageChange={(index) => list.setPage(index + 1)}
            pageSizeControl={{
              value: list.pageSize,
              onChange: list.setPageSize,
              options: [...list.pageSizes],
            }}
          />
        </Card>
      </PageShell>

      <ConfirmModal
        isOpen={!!deleting}
        title="Delete persona"
        message={`Delete '${deleting?.name ?? ''}'?`}
        confirmText={deleting?.name}
        confirmLabel="Delete persona"
        variant="danger"
        dangerNote="Every placement that references this persona stops using it. This cannot be undone."
        testId="personas-delete"
        onClose={() => setDeleting(null)}
        onConfirm={remove}
      />
    </>
  );
}
